import React, { useState } from 'react';
import { Lock, Unlock, AlertTriangle } from 'lucide-react';
import { Modal, Button, Pill, TrustChip } from '../../index';

/**
 * ProbationModal — place / release an agent on probation.
 *
 * Two modes share a single component because the operator action and
 * required confirmation are symmetric:
 *
 *   mode='place'   — Place on probation. Requires reason + optional
 *                    expiry (defaults to 7 days from now). Triggers a
 *                    full autonomy regression to PROBATION level until
 *                    expiry or release.
 *   mode='release' — Release from probation. Requires reason
 *                    documenting why the operator believes recovery
 *                    is complete. Returns agent to its prior autonomy
 *                    level.
 *
 * Phase A3 / G303. Wires to triggerProbation / releaseProbation API
 * helpers via the onSubmit callback so callers control toasting and
 * post-submit refresh.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {'place'|'release'} props.mode
 * @param {Object} props.agent           — { id, name, trustScore, autonomy, probationUntil }
 * @param {Function} props.onSubmit      — async ({ reason, expiresAt }) => void
 */
export function ProbationModal({ isOpen, onClose, mode = 'place', agent, onSubmit }) {
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 16);  // datetime-local format
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!agent) return null;

  const isPlace = mode === 'place';
  const title = isPlace ? 'Place on probation' : 'Release from probation';
  const action = isPlace ? 'Place on probation' : 'Release from probation';

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Reason is required for the audit trail.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit?.({
        reason: reason.trim(),
        expiresAt: isPlace ? new Date(expiresAt).toISOString() : null,
      });
      setReason('');
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Failed to update probation status.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      dismissOnBackdrop={!submitting}
      size="md"
      title={
        <span className="inline-flex items-center gap-2">
          {isPlace ? <Lock size={18} className="text-v2-danger-strong" /> : <Unlock size={18} className="text-v2-success-strong" />}
          {title}
        </span>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant={isPlace ? 'danger' : 'primary'} onClick={handleSubmit} disabled={submitting || !reason.trim()}>
            {submitting ? 'Submitting…' : action}
          </Button>
        </>
      }
    >
      {/* Agent context — operator sees who they're acting on. */}
      <div className="bg-v2-surface-muted rounded-v2-md p-3 mb-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-v2-ink text-[14px] truncate">{agent.name}</div>
          <div className="text-[11px] text-v2-ink-muted truncate">
            {agent.autonomy || agent.autonomyLevel || 'No autonomy set'} · ID {String(agent.id).slice(0, 8)}…
          </div>
        </div>
        {/* Canonical complianceScore (null → Unrated), not the trustScore baseline. */}
        <TrustChip score={agent.complianceScore} autonomy={agent.autonomy} size="sm" />
      </div>

      {/* Mode-specific explanation. Probation changes the runtime
          autonomy floor, so we say it in plain language. */}
      <div className={`rounded-v2-md p-3 mb-4 text-[12.5px] flex items-start gap-2 ${
        isPlace ? 'bg-v2-danger-soft text-v2-danger-strong border border-v2-danger/30'
                : 'bg-v2-success-soft text-v2-success-strong border border-v2-success/30'
      }`}>
        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
        <div>
          {isPlace ? (
            <>
              <strong>This regresses the agent to PROBATION autonomy until release or expiry.</strong>
              {' '}All future requests are forced through human approval. Skills marked
              autonomous-eligible are temporarily locked.
            </>
          ) : (
            <>
              <strong>This restores the agent's prior autonomy level immediately.</strong>
              {' '}Verify the trust score has actually recovered before releasing — releases
              are not automatic and are recorded against the operator.
            </>
          )}
        </div>
      </div>

      {/* Reason — audit field, always required */}
      <label className="block mb-3">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
          Reason <span className="text-v2-danger-strong">*</span>
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isPlace
            ? 'e.g. "Three consecutive policy violations in /skills/transfer-funds. Investigating root cause."'
            : 'e.g. "Score recovered to 92 across 14 days of clean evaluations. Approved by Risk team."'}
          rows={3}
          className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] text-v2-ink bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none resize-y"
          disabled={submitting}
        />
        <span className="text-[10.5px] text-v2-ink-muted mt-1 block">
          Persisted to the audit log alongside your user ID and timestamp.
        </span>
      </label>

      {isPlace && (
        <label className="block mb-2">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Expires
          </span>
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] text-v2-ink bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none"
            disabled={submitting}
          />
          <span className="text-[10.5px] text-v2-ink-muted mt-1 block">
            Probation auto-releases at this time. Clear for indefinite probation.
          </span>
        </label>
      )}

      {error && (
        <div className="mt-3 bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger-strong">
          {error}
        </div>
      )}
    </Modal>
  );
}

export default ProbationModal;
